import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { paymentKeys } from "./keys";
import { sponsorshipKeys } from "../sponsorships/keys";
import {
  createPayment,
  deletePayment,
  getAllPayments,
  updatePayment,
  verifyPayment,
  unverifyPayment,
  getChapterPayments,
  getPaymentStats,
  getFinancePayments,
} from "./api";
import { toast } from "sonner";

export const usePayments = (params = {}, options = {}) => {
  return useQuery({
    queryKey: paymentKeys.list(params),
    queryFn: () => getAllPayments(params),
    staleTime: 2 * 60 * 1000, // 2 min — avoids redundant refetches
    ...options,
  });
};

export const useFinancePayments = (options = {}) => {
  return useQuery({
    queryKey: paymentKeys.finance(),
    queryFn: getFinancePayments,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
};

export const usePaymentStats = (options = {}) => {
  return useQuery({
    queryKey: ["paymentStats"],
    queryFn: getPaymentStats,
    ...options,
  });
};

export const useCreatePayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      toast.success("Payment created successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to create payment");
    },
  });
};

export const useUpdatePayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updatePayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      // updating a sponsorship-linked payment amount changes sponsorship paidAmount
      queryClient.invalidateQueries({ queryKey: sponsorshipKeys.list() });
      toast.success("Payment updated successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update payment");
    },
  });
};

export const useDeletePayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      toast.success("Payment deleted successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to delete payment");
    },
  });
};

export const useVerifyPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: verifyPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      toast.success("Payment verified successfully");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to verify payment");
    },
  });
};

export const useUnverifyPayment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: unverifyPayment,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: paymentKeys.lists() });
      toast.success("Payment unverified successfully");
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to unverify payment",
      );
    },
  });
};

export const useChapterPayments = () => {
  return useQuery({
    queryKey: paymentKeys.chapterList(),
    queryFn: getChapterPayments,
  });
};
